import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * The invoice's PDF over the whole window (JBWEB-1389), opened from the
 * preview's « Agrandir ». Not a modal of the shared system: that one shows a
 * single modal at a time, under its own context, and this one opens over the
 * slide-over. Stacked like the post-it dialog: above the slide-over (z-30)
 * and the shared modal (z-40 / z-50), below the expired-session dialog
 * (z-[59] / z-[60]). Its tooltips must sit above it: TOOLTIP_Z_INDEX.
 *
 * Rendered through a portal, the dialog stays a child of the panel in the
 * React tree: its clicks reach the slide-over's panel, which stops them
 * before its overlay closes it.
 *
 * `actions` are the header's buttons, `children` the viewer. The opener gets
 * the focus back in `onClose`.
 */
export const TOOLTIP_Z_INDEX = 57;

const InvoicePdfFullscreen = ({ title, actions, onClose, children }) => {
  const dialogRef = useRef(null);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[55] bg-gray-900 opacity-70"
        onClick={onClose}
        aria-hidden
      />
      <div className="fixed inset-0 z-[56] flex items-center justify-center pointer-events-none">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          tabIndex={-1}
          className="pointer-events-auto flex flex-col w-[95vw] h-[95vh] rounded-lg bg-white shadow-xl overflow-hidden outline-none"
        >
          <div className="flex-shrink-0 flex items-center gap-2 px-5 py-3 border-b border-gray-100">
            <h2 className="flex-1 min-w-0 truncate ft-lg font-semibold text-[#202124]">
              {title}
            </h2>
            {actions}
          </div>
          {children}
        </div>
      </div>
    </>,
    document.body,
  );
};

export default InvoicePdfFullscreen;
